import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import DeviceScheduleTimeline from '@/components/shared/DeviceScheduleTimeline';
import { Card } from '@/components/ui/Card';
import Select from '@/components/ui/Select/Select';
import { apiService } from '@/utils/api';
import useGlobalDeviceSelector from '@/hooks/useGlobalDeviceSelector';
import type { ScheduleOverviewResponse, Manual, Aux, Schedule as NewSchedule } from '@/types/dashboard';

// 旧时间轴组件所需的本地 Schedule 结构
interface LegacySchedule {
  ID: number;
  DeviceID: string;
  Index: number;
  Name: string;
  PumpSpeed: number;
  AuxType: number;
  AuxSlot: number;
  IsScheduleOn: boolean;
  StartTime: number;
  EndTime: number;
  WeekDay?: number;
  WeekdaysArray?: boolean[];
}

interface AuxDevice {
  ID: number;
  DeviceID: string;
  Name: string;
  Slot: number;
  Type: number;
  IsTrump: boolean;
  IsOn: boolean;
}

interface DeviceScheduleViewProps {
  title?: string;
  selectedDeviceId?: string;
}

// 获取设备颜色的辅助函数
const getDeviceColorBySlot = (auxSlot: number, auxType: number): string => {
  // 设备颜色列表
  const defaultColors = [
    '#3B82F6', // blue
    '#10B981', // emerald
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // violet
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#65A30D', // lime
    '#0EA5E9', // light blue
    '#D946EF', // fuchsia
  ];
  
  return defaultColors[auxSlot % defaultColors.length];
};

const DeviceScheduleView: React.FC<DeviceScheduleViewProps> = ({ 
  title = "Device Schedule View",
  selectedDeviceId = ""
}) => {
  const { selectedDeviceId: globalDeviceId, onDeviceChange: globalDeviceChange } = useGlobalDeviceSelector();
  const [selectedAuxSlot, setSelectedAuxSlot] = useState<number | ''>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<LegacySchedule[]>([]);
  const [auxDevices, setAuxDevices] = useState<AuxDevice[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 监听容器尺寸变化
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      // 强制重新渲染以适应新的容器尺寸
      const event = new Event('resize');
      window.dispatchEvent(event);
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  // 获取设备数据（新版 scheduleOverview 接口）
  useEffect(() => {
    if (!globalDeviceId) return;

    const fetchDeviceData = async () => {
      setLoading(true);
      try {
        const resp = await apiService.get<ScheduleOverviewResponse>(`/api/v1/iot-shadow/device/${globalDeviceId}/scheduleOverview`);

        // 将新接口的 Schedule 映射到旧时间轴组件需要的结构
        const mapToLegacy = (s: NewSchedule): LegacySchedule => ({
          ID: s.schedule_id,
          DeviceID: globalDeviceId,
          Index: s.schedule_num ?? s.schedule_id,
          Name: s.schedule_name,
          PumpSpeed: s.pump_speed ?? 0,
          AuxType: s.aux_type_code,
          AuxSlot: s.aux_slot ?? s.slot_num ?? 0,
          IsScheduleOn: !!s.schedule_state,
          StartTime: s.start_time,
          EndTime: s.end_time,
          WeekdaysArray: s.week_days || Array(7).fill(false),
        });

        const processedSchedules: LegacySchedule[] = (resp.scheduleInfo?.schedules || []).map(mapToLegacy);
        setSchedules(processedSchedules);

        // 生成 AUX 设备列表：合并 manual 中的设备集合
        const manual: Manual = resp.manual;
        const collections: Aux[] = [
          ...(manual?.aux || []),
          ...(manual?.pump_devices || []),
          ...(manual?.spa || []),
          ...(manual?.pump_linked_devices || []),
          ...(manual?.spa_devices || []),
          ...(manual?.other_devices || []),
        ];

        const auxMap = new Map<number, Aux>();
        for (const a of collections) {
          // 以 slot 为键，后出现的覆盖之前的（同一 slot 以最新信息为准）
          auxMap.set(a.aux_slot_num, a);
        }
        const mergedAuxDevices: AuxDevice[] = Array.from(auxMap.entries()).map(([slot, a], idx) => ({
          ID: idx + 1,
          DeviceID: globalDeviceId,
          Name: a.aux_tag,
          Slot: slot,
          Type: a.aux_type_code,
          IsTrump: !!a.aux_trump,
          IsOn: !!a.aux_status,
        }));
        setAuxDevices(mergedAuxDevices);
      } catch (err) {
        console.error('Error fetching device data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDeviceData();
  }, [globalDeviceId]);

  // 设备选择 - 使用全局设备选择器
  const handleDeviceChange = (deviceId: string) => {
    globalDeviceChange(deviceId);
    setSelectedAuxSlot(''); // Reset aux device when main device changes
  };

  const handleAuxDeviceChange = (e: any) => {
    setSelectedAuxSlot(e ? e.value : '');
  };

  // Filter schedules based on selected aux device slot
  const filteredSchedules = selectedAuxSlot === ''
    ? schedules  // Show all devices when nothing is selected
    : schedules.filter(schedule => schedule.AuxSlot === Number(selectedAuxSlot));

  // 设备名称显示逻辑
  const getDeviceName = (): string => {
    if (selectedAuxSlot === '') {
      return 'All Devices';
    }
    return auxDevices.find(aux => aux.Slot === Number(selectedAuxSlot))?.Name || 'Unknown Device';
  };

  // 转换设备为Select选项格式，并去重
  const deviceOptions = [
    { value: '', label: 'All Devices' },
    ...Array.from(new Set(schedules.map(s => s.AuxSlot))).map(auxSlot => {
      const schedule = schedules.find(s => s.AuxSlot === auxSlot);
      return {
        value: auxSlot,
        label: schedule?.Name || `Device ${auxSlot}`
      };
    })
  ];

  return (
    <Card 
      bodyClass="p-4"
      bordered
      className="w-full"
      ref={containerRef}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: 2, 
          mb: 1,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          
          {/* 子设备选择器 */}
          {auxDevices.length > 0 && (
            <>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '100px'
                }}
              >
                Filter AUX:
              </Typography>
              <div style={{ minWidth: '200px' }}>
                <Select
                  placeholder="Select Device"
                  options={deviceOptions}
                  value={deviceOptions.find(option => option.value === selectedAuxSlot)}
                  onChange={handleAuxDeviceChange}
                  size="sm"
                />
              </div>
            </>
          )}
            
            {/* 设备图例 */}
          {/* {filteredSchedules.length > 0 && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              flexWrap: 'wrap', 
              gap: 2 
            }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                Device Legend:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {filteredSchedules.reduce((acc: JSX.Element[], schedule) => {
                  // 如果这个设备已经在图例中，则跳过
                  if (acc.find(item => item.key === schedule.AuxSlot.toString())) {
                    return acc;
                  }

                  let deviceName = '';
                  if (schedule.AuxSlot === 0) {
                    deviceName = 'Main Pump';
                  } else {
                    const auxDevice = auxDevices.find(d => d.Slot === schedule.AuxSlot);
                    deviceName = auxDevice?.Name || `Device ${schedule.AuxSlot}`;
                  }

                  acc.push(
                    <Box 
                      key={schedule.AuxSlot} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: '0.75rem',
                        color: 'text.secondary'
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: 10, 
                          height: 10, 
                          borderRadius: '3px', 
                          backgroundColor: getDeviceColorBySlot(schedule.AuxSlot, schedule.AuxType),
                        }} 
                      />
                      <span>{deviceName}</span>
                    </Box>
                  );
                  return acc;
                }, [])}
              </Box>
            </Box>
          )} */}
        </Box>

        {!globalDeviceId ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Please select a device to view schedules
            </Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ 
            height: '80vh', 
            minHeight: '40vh',
            width: '100%',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            '& .apexcharts-xaxis': {
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: 'background.paper',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }
          }}>
            <DeviceScheduleTimeline 
              schedules={Array(7).fill(0).map((_, dayIndex) => 
                filteredSchedules.filter(schedule => 
                  schedule.WeekdaysArray?.[dayIndex]
                )
              )} 
              deviceName={getDeviceName()} 
            />
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default DeviceScheduleView;